/**
 * Deduce `nextRunAt` (ISO-8601 UTC) from automation scheduled trigger `schedule.rRule` (iCalendar VEVENT).
 */

import {
	SCHEDULED_CHAT_DAYS,
	type ScheduledChatDayOfWeek,
	type ScheduledChatFrequency,
} from '@atlassian/conversation-assistant-message-actions-api/scheduled-chat';

type ParsedDtStart = {
	tzid?: string;
	y: number;
	mo: number;
	d: number;
	h: number;
	mi: number;
	s: number;
};

type ParsedRRule = {
	freq: 'MINUTELY' | 'HOURLY' | 'DAILY' | 'WEEKLY' | 'MONTHLY' | 'YEARLY' | string;
	interval: number;
	/** RRULE BYHOUR / BYMINUTE / BYSECOND (else DTSTART time is used). */
	byHour?: number;
	byMinute?: number;
	bySecond?: number;
	/** Two-letter days from BYDAY (e.g. `TH`); ordinals like `2TH` are reduced to `TH`. */
	byDays?: string[];
	/** RRULE BYMONTHDAY (1–31); first value if comma-separated. Used for `FREQ=MONTHLY`. */
	byMonthDay?: number;
};

export type ParsedScheduleBlock = {
	dtStart: ParsedDtStart;
	rrule: ParsedRRule;
};

/** Read trigger.value.schedule.rRule from a rule object (full rule or ruleSummary). */
export function getScheduleRRuleString(rule: Record<string, unknown>): string | undefined {
	const trigger = rule.trigger as Record<string, unknown> | undefined;
	const value = trigger?.value as Record<string, unknown> | undefined;
	const schedule = value?.schedule as Record<string, unknown> | undefined;
	const raw = schedule?.rRule ?? schedule?.rrule;
	return typeof raw === 'string' && raw.trim() !== '' ? raw : undefined;
}

function parseDtStartLine(line: string): ParsedDtStart | null {
	const tzMatch = line.match(/TZID=([^;:]+)[:;]/);
	const tzid = tzMatch?.[1]?.trim();
	const dtMatch = line.match(/(\d{8}T\d{6})/);
	if (!dtMatch) {
		return null;
	}
	const compact = dtMatch[1];
	const y = Number(compact.slice(0, 4));
	const mo = Number(compact.slice(4, 6));
	const d = Number(compact.slice(6, 8));
	const h = Number(compact.slice(9, 11));
	const mi = Number(compact.slice(11, 13));
	const s = Number(compact.slice(13, 15));
	if ([y, mo, d, h, mi, s].some((n) => Number.isNaN(n))) {
		return null;
	}
	return { tzid, y, mo, d, h, mi, s };
}

function parseRRuleLine(line: string): ParsedRRule | null {
	const body = line.startsWith('RRULE:') ? line.slice('RRULE:'.length) : line;
	const parts = body.split(';').map((p) => p.trim());
	const map: Record<string, string> = {};
	for (const p of parts) {
		const eq = p.indexOf('=');
		if (eq === -1) {
			continue;
		}
		map[p.slice(0, eq).toUpperCase()] = p.slice(eq + 1);
	}
	const freq = (map.FREQ ?? '').toUpperCase();
	if (!freq) {
		return null;
	}
	const interval = Math.max(1, Number.parseInt(map.INTERVAL ?? '1', 10) || 1);
	const byHour = map.BYHOUR !== undefined ? Number.parseInt(map.BYHOUR, 10) : undefined;
	const byMinute = map.BYMINUTE !== undefined ? Number.parseInt(map.BYMINUTE, 10) : undefined;
	const bySecond = map.BYSECOND !== undefined ? Number.parseInt(map.BYSECOND, 10) : undefined;
	const byDayRaw = map.BYDAY;
	const byDays = byDayRaw
		? byDayRaw
				.split(',')
				.map((x) => parseByDayTwoLetter(x.trim()))
				.filter((x): x is string => x !== undefined)
		: undefined;
	const byMonthDayRaw = map.BYMONTHDAY;
	const byMonthDayParsed =
		byMonthDayRaw !== undefined
			? Number.parseInt(byMonthDayRaw.split(',')[0] ?? '', 10)
			: undefined;
	return {
		freq,
		interval,
		byHour: Number.isFinite(byHour) ? byHour : undefined,
		byMinute: Number.isFinite(byMinute) ? byMinute : undefined,
		bySecond: Number.isFinite(bySecond) ? bySecond : undefined,
		byDays: byDays?.length ? byDays : undefined,
		byMonthDay: Number.isFinite(byMonthDayParsed) ? byMonthDayParsed : undefined,
	};
}

/** `TH`, `2TH`, `-1MO` → `TH` / `MO`. */
function parseByDayTwoLetter(raw: string): string | undefined {
	const m = raw.match(/^(-?\d+)?([A-Z]{2})$/i);
	const code = (m?.[2] ?? raw.slice(-2)).toUpperCase();
	return /^[A-Z]{2}$/.test(code) ? code : undefined;
}

function bydayTwoLetterToJsWeekday(twoLetter: string): number {
	const m: Record<string, number> = { SU: 0, MO: 1, TU: 2, WE: 3, TH: 4, FR: 5, SA: 6 };
	return m[twoLetter.slice(0, 2).toUpperCase()] ?? -1;
}

function jsWeekdayToBydayCode(weekdaySun0: number): string {
	return ['SU', 'MO', 'TU', 'WE', 'TH', 'FR', 'SA'][weekdaySun0] ?? 'MO';
}

function getYmdInZone(ms: number, timeZone: string): { y: number; mo: number; d: number } {
	const fmt = new Intl.DateTimeFormat('en-US', {
		timeZone,
		year: 'numeric',
		month: 'numeric',
		day: 'numeric',
	});
	const parts = fmt.formatToParts(new Date(ms));
	const g = (ty: Intl.DateTimeFormatPartTypes) => Number(parts.find((p) => p.type === ty)?.value);
	return { y: g('year'), mo: g('month'), d: g('day') };
}

/** Next calendar date after `(y,mo,d)` in `timeZone` (noon-based step avoids some DST edges). */
function nextCalendarDate(
	y: number,
	mo: number,
	d: number,
	timeZone: string,
): { y: number; mo: number; d: number } {
	const noon = wallTimeToUtcMs({ tzid: timeZone, y, mo, d, h: 12, mi: 0, s: 0 }, timeZone);
	for (const addH of [22, 26, 30]) {
		const cand = noon + addH * 3_600_000;
		const n = getYmdInZone(cand, timeZone);
		if (n.y !== y || n.mo !== mo || n.d !== d) {
			return n;
		}
	}
	return getYmdInZone(noon + 48 * 3_600_000, timeZone);
}

function zonedWeekdaySun0(ms: number, timeZone: string): number {
	const w = new Intl.DateTimeFormat('en-US', {
		timeZone,
		weekday: 'short',
	}).format(new Date(ms));
	const map: Record<string, number> = { Sun: 0, Mon: 1, Tue: 2, Wed: 3, Thu: 4, Fri: 5, Sat: 6 };
	return map[w] ?? -1;
}

/**
 * Next instant ≥ `nowMs` that falls on `targetWeekdaySun0` at `hour`:`minute`:`second` in `timeZone`.
 */
function findNextWeeklyWallTime(
	timeZone: string | undefined,
	targetWeekdaySun0: number,
	hour: number,
	minute: number,
	second: number,
	nowMs: number,
): number | undefined {
	const zone = timeZone ?? 'UTC';
	let { y, mo, d } = getYmdInZone(nowMs, zone);
	for (let i = 0; i < 400; i++) {
		const t = wallTimeToUtcMs(
			{ tzid: timeZone, y, mo, d, h: hour, mi: minute, s: second },
			timeZone,
		);
		const wd = zonedWeekdaySun0(t, zone);
		if (wd === targetWeekdaySun0 && t >= nowMs) {
			return t;
		}
		const n = nextCalendarDate(y, mo, d, zone);
		y = n.y;
		mo = n.mo;
		d = n.d;
	}
	return undefined;
}

function resolveWeeklyByDays(
	dtStart: ParsedDtStart,
	rrule: ParsedRRule,
	nowMs: number,
): string[] | undefined {
	if (rrule.byDays?.length) {
		return rrule.byDays;
	}
	const zone = dtStart.tzid ?? 'UTC';
	const t0 = wallTimeToUtcMs(dtStart, dtStart.tzid);
	const wd = zonedWeekdaySun0(t0, zone);
	if (wd < 0) {
		return undefined;
	}
	return [jsWeekdayToBydayCode(wd)];
}

function nextRunMsForWeekly(
	dtStart: ParsedDtStart,
	rrule: ParsedRRule,
	nowMs: number,
): number | undefined {
	if (rrule.interval !== 1) {
		return undefined;
	}
	const hour = rrule.byHour ?? dtStart.h;
	const minute = rrule.byMinute ?? dtStart.mi;
	const second = rrule.bySecond ?? dtStart.s;
	const dayCodes = resolveWeeklyByDays(dtStart, rrule, nowMs);
	if (!dayCodes?.length) {
		return undefined;
	}
	const candidates: number[] = [];
	for (const code of dayCodes) {
		const wd = bydayTwoLetterToJsWeekday(code);
		if (wd < 0) {
			continue;
		}
		const t = findNextWeeklyWallTime(dtStart.tzid, wd, hour, minute, second, nowMs);
		if (t !== undefined) {
			candidates.push(t);
		}
	}
	if (candidates.length === 0) {
		return undefined;
	}
	return Math.min(...candidates);
}

function daysInMonth(year: number, month1to12: number): number {
	return new Date(year, month1to12, 0).getDate();
}

function addCalendarMonths(
	year: number,
	month1to12: number,
	deltaMonths: number,
): { y: number; mo: number } {
	const idx = year * 12 + (month1to12 - 1) + deltaMonths;
	const y = Math.floor(idx / 12);
	const mo = (idx % 12) + 1;
	return { y, mo };
}

/**
 * `FREQ=MONTHLY` with BYMONTHDAY (or day-of-month from DTSTART). BYDAY forms (e.g. second Tuesday) are not supported.
 */
function nextRunMsForMonthly(
	dtStart: ParsedDtStart,
	rrule: ParsedRRule,
	nowMs: number,
): number | undefined {
	if (rrule.byDays?.length) {
		return undefined;
	}
	const interval = Math.max(1, rrule.interval);
	const hour = rrule.byHour ?? dtStart.h;
	const minute = rrule.byMinute ?? dtStart.mi;
	const second = rrule.bySecond ?? dtStart.s;
	const desiredDom = rrule.byMonthDay ?? dtStart.d;
	if (desiredDom < 1 || desiredDom > 31) {
		return undefined;
	}
	const tz = dtStart.tzid;

	for (let k = 0; k < 120; k++) {
		const { y, mo } = addCalendarMonths(dtStart.y, dtStart.mo, k * interval);
		const dim = daysInMonth(y, mo);
		const dom = Math.min(desiredDom, dim);
		const t = wallTimeToUtcMs({ tzid: tz, y, mo, d: dom, h: hour, mi: minute, s: second }, tz);
		if (t >= nowMs) {
			return t;
		}
	}
	return undefined;
}

/** Parse `BEGIN:VEVENT` … `END:VEVENT` block (newlines in string). */
export function parseVeventScheduleBlock(raw: string): ParsedScheduleBlock | null {
	const lines = raw.split(/\n/).map((l) => l.trim());
	let dtStart: ParsedDtStart | null = null;
	let rrule: ParsedRRule | null = null;
	for (const line of lines) {
		if (line.startsWith('DTSTART')) {
			dtStart = parseDtStartLine(line);
		} else if (line.startsWith('RRULE')) {
			rrule = parseRRuleLine(line);
		}
	}
	if (!dtStart || !rrule) {
		return null;
	}
	return { dtStart, rrule };
}

function wallTimeToUtcMs(dt: ParsedDtStart, timeZone: string | undefined): number {
	const { y, mo, d, h, mi, s } = dt;
	if (!timeZone) {
		return Date.UTC(y, mo - 1, d, h, mi, s);
	}
	const fmt = new Intl.DateTimeFormat('en-US', {
		timeZone,
		year: 'numeric',
		month: '2-digit',
		day: '2-digit',
		hour: '2-digit',
		minute: '2-digit',
		second: '2-digit',
		hourCycle: 'h23',
	});
	const read = (epoch: number) => {
		const parts = fmt.formatToParts(new Date(epoch));
		const g = (ty: Intl.DateTimeFormatPartTypes) => Number(parts.find((p) => p.type === ty)?.value);
		return {
			y: g('year'),
			mo: g('month'),
			d: g('day'),
			h: g('hour'),
			mi: g('minute'),
			s: g('second'),
		};
	};
	let t = Date.UTC(y, mo - 1, d, h, mi, s);
	for (let i = 0; i < 120; i++) {
		const p = read(t);
		if (p.y === y && p.mo === mo && p.d === d && p.h === h && p.mi === mi && p.s === s) {
			return t;
		}
		t += ((h - p.h) * 3600 + (mi - p.mi) * 60 + (s - p.s)) * 1000;
	}
	return Date.UTC(y, mo - 1, d, h, mi, s);
}

function periodMsForFreq(freq: string, interval: number): number | null {
	switch (freq) {
		case 'MINUTELY':
			return 60_000 * interval;
		case 'HOURLY':
			return 3_600_000 * interval;
		case 'DAILY':
			return 86_400_000 * interval;
		default:
			return null;
	}
}

/**
 * Max distance from a scheduled slot instant to a SUCCESS audit timestamp when pairing run history.
 * Uses half the smallest gap between consecutive anticipated slots (from expansion), minus 1ms, so one
 * SUCCESS cannot match two neighboring fires. If there is only one slot in range, falls back to nominal
 * period from FREQ/INTERVAL (and WEEKLY/MONTHLY heuristics), then `fallbackMs`.
 */
export function getSuccessAuditSlotMatchWindowMs(
	slotTimestampsMs: readonly number[],
	rawSchedule: string,
	fallbackMs: number,
): number {
	const sorted = [...new Set(slotTimestampsMs)].sort((a, b) => a - b);
	if (sorted.length >= 2) {
		let minGap = Infinity;
		for (let i = 1; i < sorted.length; i++) {
			const g = sorted[i] - sorted[i - 1];
			if (g > 0 && g < minGap) {
				minGap = g;
			}
		}
		if (Number.isFinite(minGap) && minGap > 0) {
			const w = Math.floor(minGap / 2) - 1;
			if (w >= 1000) {
				return w;
			}
		}
	}
	const parsed = parseVeventScheduleBlock(rawSchedule);
	if (parsed) {
		const { rrule } = parsed;
		const interval = Math.max(1, rrule.interval);
		if (rrule.freq === 'WEEKLY') {
			const periodMs = 7 * 86_400_000 * interval;
			return Math.max(1000, Math.floor(periodMs / 2) - 1);
		}
		if (rrule.freq === 'MONTHLY') {
			const periodMs = 28 * 86_400_000 * interval;
			return Math.max(1000, Math.floor(periodMs / 2) - 1);
		}
		const period = periodMsForFreq(rrule.freq, interval);
		if (period !== null) {
			return Math.max(1000, Math.floor(period / 2) - 1);
		}
	}
	return fallbackMs;
}

function nextAlignedOccurrenceMs(startMs: number, periodMs: number, nowMs: number): number {
	if (nowMs <= startMs) {
		return startMs;
	}
	const steps = Math.ceil((nowMs - startMs) / periodMs);
	return startMs + steps * periodMs;
}

/**
 * Returns ISO UTC string for the next run after "now", or `undefined` if rRule is missing / unsupported.
 */
export function deduceNextRunAtIsoFromRRuleRaw(
	raw: string | undefined,
	nowMs: number = Date.now(),
): string | undefined {
	if (!raw?.trim()) {
		return undefined;
	}
	const parsed = parseVeventScheduleBlock(raw);
	if (!parsed) {
		return undefined;
	}
	const { dtStart, rrule } = parsed;
	if (rrule.freq === 'WEEKLY') {
		const nextMs = nextRunMsForWeekly(dtStart, rrule, nowMs);
		return nextMs !== undefined ? new Date(nextMs).toISOString() : undefined;
	}
	if (rrule.freq === 'MONTHLY') {
		const nextMs = nextRunMsForMonthly(dtStart, rrule, nowMs);
		return nextMs !== undefined ? new Date(nextMs).toISOString() : undefined;
	}
	const period = periodMsForFreq(rrule.freq, rrule.interval);
	if (period === null) {
		return undefined;
	}

	const adjustedDtStart = {
		...dtStart,
		h: rrule.byHour ?? dtStart.h,
		mi: rrule.byMinute ?? dtStart.mi,
		s: rrule.bySecond ?? dtStart.s,
	};
	const startMs = wallTimeToUtcMs(adjustedDtStart, adjustedDtStart.tzid);
	const nextMs = nextAlignedOccurrenceMs(startMs, period, nowMs);
	return new Date(nextMs).toISOString();
}

export function deduceNextRunAtIsoFromRule(rule: Record<string, unknown>): string | undefined {
	const raw = getScheduleRRuleString(rule);
	return deduceNextRunAtIsoFromRRuleRaw(raw);
}

const MAX_SCHEDULED_OCCURRENCES = 5000;

/** Millis from `rule.created` when the API sends it (Date, epoch s/ms, or ISO). */
export function getRuleCreatedMsFromRule(rule: Record<string, unknown>): number | undefined {
	const c = rule.created as unknown;
	if (c instanceof Date) {
		const n = c.getTime();
		return Number.isNaN(n) ? undefined : n;
	}
	if (typeof c === 'number' && Number.isFinite(c)) {
		return c < 1e12 ? c * 1000 : c;
	}
	if (typeof c === 'string') {
		const n = Date.parse(c);
		return Number.isNaN(n) ? undefined : n;
	}
	return undefined;
}

/**
 * Lower bound for “anticipated” occurrences: rule creation if known, else DTSTART from the VEVENT.
 */
export function resolveScheduleHistoryStartMs(
	scheduleRRule: string,
	ruleCreatedAtIso?: string,
): number | undefined {
	const parsed = parseVeventScheduleBlock(scheduleRRule);
	if (!parsed) {
		return undefined;
	}
	if (ruleCreatedAtIso) {
		const n = Date.parse(ruleCreatedAtIso);
		if (Number.isFinite(n)) {
			return n;
		}
	}
	return wallTimeToUtcMs(parsed.dtStart, parsed.dtStart.tzid);
}

function listFixedPeriodOccurrencesBetween(
	dtStart: ParsedDtStart,
	periodMs: number,
	ruleCreatedMs: number,
	untilMs: number,
): number[] {
	const startMs = wallTimeToUtcMs(dtStart, dtStart.tzid);
	const out: number[] = [];
	let t = startMs;
	if (t < ruleCreatedMs) {
		const steps = Math.ceil((ruleCreatedMs - t) / periodMs);
		t = startMs + steps * periodMs;
	}
	while (t <= untilMs && out.length < MAX_SCHEDULED_OCCURRENCES) {
		out.push(t);
		t += periodMs;
	}
	return out;
}

function listWeeklyOccurrencesBetween(
	dtStart: ParsedDtStart,
	rrule: ParsedRRule,
	ruleCreatedMs: number,
	untilMs: number,
): number[] {
	if (rrule.interval !== 1) {
		return [];
	}
	const out: number[] = [];
	let cur = ruleCreatedMs;
	for (let i = 0; i < MAX_SCHEDULED_OCCURRENCES; i++) {
		const next = nextRunMsForWeekly(dtStart, rrule, cur);
		if (next === undefined || next > untilMs) {
			break;
		}
		out.push(next);
		cur = next + 1000;
	}
	return out;
}

function listMonthlyOccurrencesBetween(
	dtStart: ParsedDtStart,
	rrule: ParsedRRule,
	ruleCreatedMs: number,
	untilMs: number,
): number[] {
	const out: number[] = [];
	let cur = ruleCreatedMs;
	for (let i = 0; i < MAX_SCHEDULED_OCCURRENCES; i++) {
		const next = nextRunMsForMonthly(dtStart, rrule, cur);
		if (next === undefined || next > untilMs) {
			break;
		}
		out.push(next);
		cur = next + 1000;
	}
	return out;
}

/**
 * Every scheduled fire time from `ruleCreatedMs` through `untilMs` (inclusive) per RRULE.
 * Unsupported shapes return [] (caller should fall back to raw audit rows).
 */
export function listScheduledOccurrencesMsBetween(
	raw: string | undefined,
	ruleCreatedMs: number,
	untilMs: number,
): number[] {
	if (!raw?.trim() || untilMs < ruleCreatedMs) {
		return [];
	}
	const parsed = parseVeventScheduleBlock(raw);
	if (!parsed) {
		return [];
	}
	const { dtStart, rrule } = parsed;
	if (rrule.freq === 'WEEKLY') {
		return listWeeklyOccurrencesBetween(dtStart, rrule, ruleCreatedMs, untilMs);
	}
	if (rrule.freq === 'MONTHLY') {
		return listMonthlyOccurrencesBetween(dtStart, rrule, ruleCreatedMs, untilMs);
	}
	const period = periodMsForFreq(rrule.freq, rrule.interval);
	if (period === null) {
		return [];
	}
	return listFixedPeriodOccurrencesBetween(dtStart, period, ruleCreatedMs, untilMs);
}

const RRULE_CODE_TO_EDITOR_DAY: Record<string, ScheduledChatDayOfWeek> = {
	SU: 'sunday',
	MO: 'monday',
	TU: 'tuesday',
	WE: 'wednesday',
	TH: 'thursday',
	FR: 'friday',
	SA: 'saturday',
};

function normalizeTwoLetterDayCode(code: string): string {
	const m = code.trim().match(/^(-?\d+)?([A-Z]{2})$/i);
	return (m?.[2] ?? code.slice(-2)).toUpperCase();
}

function codesToSortedEditorDays(codes: string[]): ScheduledChatDayOfWeek[] {
	const mapped = codes
		.map((c) => RRULE_CODE_TO_EDITOR_DAY[normalizeTwoLetterDayCode(c)])
		.filter((d): d is ScheduledChatDayOfWeek => d !== undefined);
	const uniq = [...new Set(mapped)];
	const dayOrder = (d: ScheduledChatDayOfWeek) => SCHEDULED_CHAT_DAYS.indexOf(d);
	return uniq.sort((a, b) => dayOrder(a) - dayOrder(b));
}

function isMonThroughFriOnly(codes: string[]): boolean {
	const set = new Set(codes.map(normalizeTwoLetterDayCode));
	if (set.size !== 5) {
		return false;
	}
	return ['MO', 'TU', 'WE', 'TH', 'FR'].every((c) => set.has(c));
}

function formatEditorTime24h(hour: number, minute: number): string {
	return `${String(hour).padStart(2, '0')}:${String(minute).padStart(2, '0')}`;
}

export type DerivedScheduledChatSchedule = {
	frequency: ScheduledChatFrequency;
	daysOfWeek: ScheduledChatDayOfWeek[];
	time: string;
};

/**
 * Maps automation `trigger.value.schedule` VEVENT / RRULE to scheduled-chat editor fields
 * (frequency, days of week, 24h time). Returns `null` if no usable schedule is present.
 */
export function deriveScheduledChatScheduleFromAutomationRule(
	rule: Record<string, unknown>,
): DerivedScheduledChatSchedule | null {
	const raw = getScheduleRRuleString(rule);
	if (!raw?.trim()) {
		return null;
	}
	const parsed = parseVeventScheduleBlock(raw);
	if (!parsed) {
		return null;
	}
	const { dtStart, rrule } = parsed;
	const hour = rrule.byHour ?? dtStart.h;
	const minute = rrule.byMinute ?? dtStart.mi;
	const time = formatEditorTime24h(hour, minute);
	const freq = rrule.freq.toUpperCase();

	if (freq === 'HOURLY' || freq === 'MINUTELY') {
		return { frequency: 'hourly', daysOfWeek: [], time };
	}

	if (freq === 'DAILY') {
		return { frequency: 'daily', daysOfWeek: [], time };
	}

	if (freq === 'WEEKLY') {
		let dayCodes: string[];
		if (rrule.byDays?.length) {
			dayCodes = rrule.byDays;
		} else {
			const resolved = resolveWeeklyByDays(dtStart, rrule, Date.now());
			if (!resolved?.length) {
				return null;
			}
			dayCodes = resolved;
		}
		if (isMonThroughFriOnly(dayCodes)) {
			return {
				frequency: 'weekdays',
				daysOfWeek: ['monday', 'tuesday', 'wednesday', 'thursday', 'friday'],
				time,
			};
		}
		const daysOfWeek = codesToSortedEditorDays(dayCodes);
		if (daysOfWeek.length === 0) {
			return null;
		}
		return { frequency: 'custom', daysOfWeek, time };
	}

	// No monthly/yearly mode in editor — surface as daily at the same wall time
	if (freq === 'MONTHLY' || freq === 'YEARLY') {
		return { frequency: 'daily', daysOfWeek: [], time };
	}

	return null;
}
